import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { DESIGN_STAGES as STAGES, stageIndex } from '../../constants/designStages';

/**
 * Loading state for design generation.
 *
 * Replaces the joke feed. That made sense when a run took ~3 minutes and the
 * backend reported nothing; runs now take ~35s and expose which Aurora job is
 * active, so we show real progress over the customer's own roof instead of
 * distracting from an opaque wait.
 *
 * Stage labels live in constants/designStages so the bottom bar shows exactly
 * the same wording.
 */

const sweep = keyframes`
  0%   { top: -30%; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { top: 100%; opacity: 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const ImageFrame = styled.div`
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #0f1720;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19);
`;

const HomeImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* The sweep reads as "we are scanning your roof" — it is decorative, but it is
   synced to real work, so it stops the moment the design is ready. */
const ScanLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 28%;
  background: linear-gradient(
    to bottom,
    rgba(45, 212, 191, 0) 0%,
    rgba(45, 212, 191, 0.22) 45%,
    rgba(45, 212, 191, 0.5) 50%,
    rgba(45, 212, 191, 0.22) 55%,
    rgba(45, 212, 191, 0) 100%
  );
  animation: ${sweep} 2.6s ease-in-out infinite;
  pointer-events: none;
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(45, 212, 191, 0.10) 1px, transparent 1px),
    linear-gradient(90deg, rgba(45, 212, 191, 0.10) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
`;

const StageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const StageRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ $state }) =>
    $state === 'done' ? '#1f2937' : $state === 'active' ? '#0f766e' : '#9ca3af'};
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 400)};
  transition: color 240ms ease;
`;

const Dot = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  color: #fff;
  background: ${({ $state }) =>
    $state === 'done' ? '#0d9488' : $state === 'active' ? '#14b8a6' : '#e5e7eb'};
  animation: ${({ $state }) => ($state === 'active' ? pulse : 'none')} 1.4s ease-in-out infinite;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #14b8a6, #0d9488);
  width: ${({ $pct }) => $pct}%;
  transition: width 600ms ease;
`;

const Caption = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
`;

const DesignProgress = ({ satelliteImage, apiRunStatus, designStatus }) => {
  // Index of the active stage. Unknown/absent apiRunStatus means the run has
  // been accepted but hasn't reported a stage yet — treat that as stage 0
  // rather than showing nothing.
  const reportedIndex = stageIndex(apiRunStatus);
  const activeIndex = reportedIndex === -1 ? 0 : reportedIndex;

  // Stages only ever move forward. Polling can briefly return a stale value
  // (or the endpoint may omit it), and a progress bar that goes backwards
  // reads as a failure.
  const [maxIndex, setMaxIndex] = useState(0);
  useEffect(() => {
    setMaxIndex((prev) => (activeIndex > prev ? activeIndex : prev));
  }, [activeIndex]);

  const stageState = (i) => {
    if (i < maxIndex) return 'done';
    if (i === maxIndex) return 'active';
    return 'todo';
  };

  // Fill to the middle of the active stage so the bar always shows motion.
  const pct = Math.min(100, ((maxIndex + 0.5) / STAGES.length) * 100);

  return (
    <Wrapper>
      {satelliteImage && (
        <ImageFrame>
          <HomeImage src={satelliteImage} alt="Satellite view of your home" />
          <Grid />
          <ScanLine />
        </ImageFrame>
      )}

      <StageList>
        {STAGES.map((stage, i) => {
          const state = stageState(i);
          return (
            <StageRow key={stage.key} $state={state}>
              <Dot $state={state}>{state === 'done' ? '✓' : ''}</Dot>
              <span>{stage.label}</span>
            </StageRow>
          );
        })}
      </StageList>

      <BarTrack>
        <BarFill $pct={pct} />
      </BarTrack>
      <Caption>Building your custom system design…</Caption>
    </Wrapper>
  );
};

export default DesignProgress;
